import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import { useConfiguration } from '../../contexts/ConfigurationContext';
import RestrictionContainer from './RestrictionContainer';

interface PasswordRestrictionContainerProps {
	isMobile?: boolean;
	onSubmitClick?: (_token: string) => void;
	playerConfigs?: any;
	tokens?: string[] | null;
}

const FieldContainer = styled.div<PasswordRestrictionContainerProps>`
	display: flex;
	align-items: center;
	justify-content: flex-start;
	${({ isMobile }) =>
		isMobile &&
		css`
			flex-direction: column;
		`}
`;

const PasswordInputLabel = styled.label<PasswordRestrictionContainerProps>`
	margin-bottom: 24px;
	display: block;
	font-weight: 600;
	${({ isMobile }) =>
		isMobile
			? css`
					font-size: 1.75em;
					text-align: center;
				`
			: css`
					font-size: 1.875em;
				`}
`;

const PasswordInput = styled.input<PasswordRestrictionContainerProps>`
	height: 42px;
	background: #ffffff;
	border: 1px solid #cdd4d9;
	border-radius: 4px;
	padding: 12px 24px;
	color: #000000 !important;
	${({ isMobile }) =>
		!isMobile
			? css`
					width: 380px;
					margin-right: 1em;
				`
			: css`
					width: 100%;
					margin-bottom: 24px;
				`}
`;

const LoginButton = styled.button<PasswordRestrictionContainerProps>`
	cursor: pointer;
	border: none;
	background: #126cfc;
	box-shadow: 0px 3px 3px #00000060;
	border-radius: 4px;
	height: 42px;
	color: #fff;
	padding: 12px 24px;
	font-weight: 600;

	&:hover {
		background: #2152d0;
	}
`;

const ErrorMessage = styled.div`
	color: #f44352;
	font-size: 0.75em;
	margin-bottom: 0.5em;
	font-weight: 600;
`;

const PasswordRestrictionContainer: React.FC<PasswordRestrictionContainerProps> = ({
	isMobile,
	onSubmitClick: onSubmitClickProps,
	playerConfigs,
	tokens
}) => {
	const { t } = useTranslation();

	const { accountId, currentMediaId, mediaType, setRestrictedToken, configParams } = useConfiguration();

	const clickedSubmit = useRef(false);
	const [password, setPassword] = useState('');
	const [currentUserIP, setCurrentUserIP] = useState('');
	const [show, setShow] = useState(false);

	const getCurrentUserIP = async () => {
		const res = await axios.get('https://api.ipify.org/?format=json');
		setCurrentUserIP(res.data.ip);
	};

	const onPasswordChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setPassword(event.target.value);
	}, []);

	const onSubmitClick = useCallback(
		(_: React.MouseEvent<HTMLButtonElement>) => {
			const expirationDate = new Date(+new Date() + 300000).toISOString();
			const unsignedToken = expirationDate + '|' + currentUserIP + '|' + accountId;
			const token = (window as any).MD5?.hash(unsignedToken, password) + '.' + (window as any).MD5.toHex(unsignedToken);
			setRestrictedToken({
				mediaId: currentMediaId,
				token: token,
			});
			onSubmitClickProps?.(token);
			setTimeout(() => clickedSubmit.current = true, 1000);
		},
		[currentUserIP, accountId, password, setRestrictedToken, currentMediaId, onSubmitClickProps]
	);

	const onKeydown = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			if (event.key === 'Enter') {
				onSubmitClick(event as any);
			}
		},
		[onSubmitClick]
	);

	useEffect(() => {
		if (configParams.qplayerPreview && !configParams.useLoader) {
			setShow(true);
			return;
		}
		if (tokens !== undefined && tokens !== null) {
			setTimeout(() => setShow(true), mediaType === "scenario" ? 1000 : 1500);
		};
	}, [tokens, mediaType, configParams.qplayerPreview, configParams.useLoader]);

	useEffect(() => {
		getCurrentUserIP();
	}, []);

	return (
		<RestrictionContainer isMobile={isMobile} playerConfigs={playerConfigs}>
			{show && <>
				<PasswordInputLabel isMobile={isMobile}>{t('LABEL_PASSWORD_MESSAGE')}</PasswordInputLabel>

				{clickedSubmit.current && (
					<ErrorMessage>
						<span>{t('LABEL_PASSWORD_ERROR')}</span>
					</ErrorMessage>
				)}

				<FieldContainer isMobile={isMobile}>
					<PasswordInput
						isMobile={isMobile}
						type="password"
						placeholder={t('LABEL_PASSWORD')}
						value={password}
						onChange={onPasswordChange}
						onKeyDown={onKeydown}
					/>
					<LoginButton isMobile={isMobile} onClick={onSubmitClick}>
						{t('LABEL_SUBMIT')}
					</LoginButton>
				</FieldContainer>
			 </>}
		</RestrictionContainer>
	);
};

export default PasswordRestrictionContainer;
